<template>
  <aw-modal
    :opened="opened"
    size="sm"
    :closable="modal.closable"
    :modal-title="$awt('aw.cold_parcel_confirm_modal.title')"
    @close-modal="closeModal"
  >
    <template #content>
      <aw-separator margin-top="none" />

      <aw-spinner v-if="isLoading" />

      <template v-else>
        <aw-notification-item
          is-margin-less
          :notification="{
            type: 'error',
            iconName: 'error-16',
            manualClose: false,
            text: {
              subtitle: notificationText
            }
          }"
          index="0"
          thick-content
        />

        <div v-if="mappedTimeframes.length > 0 && fromModal" :class="$style.timeframeSelectionInfoText">
          {{ $awt('aw.cold_parcel_confirm_modal.from_dm_modal.above_timeframe_selector_text') }}
        </div>

        <div v-if="mappedTimeframes.length > 0" :class="$style.timeframeSelection">
          <aw-select-version2
            v-model:selected-options="selectedTimeframe"
            tabindex="0"
            :class="$style.timeframeSelector"
            :inline-label-text="$awt('aw.cold_parcel_confirm_modal.choose_time_frame')"
            :multiple="false"
            dynamic-width
            :options="mappedTimeframes"
            :dropdown-icon-attrs="{
              name: 'arrow-down-16',
              size: 14
            }"
          />
        </div>

        <aw-separator />

        <div :class="$style.buttonContainer">
          <lv-button
            v-for="button in buttons"
            :key="button.id"
            v-bind="button.attributes"
            @click="button.action"
          >
            {{ button.label }}
          </lv-button>
        </div>
      </template>
    </template>
  </aw-modal>
</template>

<script setup>
  import { computed, defineAsyncComponent, ref, watch } from 'vue';
  import { navigateTo, useNuxtApp } from 'nuxt/app';
  import { storeToRefs } from 'pinia';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useModalStore } from '~~/common/stores/modal';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery.js';
  import { DM_DELIVERY_COOLOMAT_LOCKER } from '~~/common/config/DeliveryMethod';
  import { DEFAULT_TIMEFRAME_ID, formatTimeframesForSelect, saveSelectedTimeframe } from '~~/common/utils/timeframeHelper';
  import modalSteps from '~~/shop/app/components/Common/Modals/AwDeliveryMethodAndTimeframeModal/enum/modalStepsEnum';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index';
  import AwModal from '~~/common/components/Common/AwModal';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import AwSpinner from '~~/common/components/Common/AwSpinner';

  const AwSelectVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwSelectVersion2'));
  const AwNotificationItem = defineAsyncComponent(() => import('~~/common/components/Common/AwNotificationItem.vue'));

  const {
    $api,
    $awt,
    $date,
    $logger,
    $localePath,
  } = useNuxtApp();

  const modalStore = useModalStore();
  const deliveryStore = useDeliveryStore();

  const { activeColdParcelConfirmModal: modal } = storeToRefs(modalStore);
  const { setup: deliveryMethod } = storeToRefs(deliveryStore);

  const isLoading = ref(false);
  const timeframes = ref([]);
  const selectedTimeframe = ref(null);

  const saveTimeFrameButton = computed(() => ({
    id: 'save-timeframe',
    label: $awt('aw.cold_parcel_confirm_modal.save_timeframe'),
    action: () => {
      saveTimeframe();
    },
    attributes: {
      styling: 'primary-dark',
      shrink: true,
      disabled: !selectedTimeframe.value || selectedTimeframe.value.id === DEFAULT_TIMEFRAME_ID,
      loadingId: 'cold-parcel-confirm-modal-save-timeframe',
    },
  }));

  const changeDeliveryMethodButton = {
    id: 'change-delivery-method',
    label: $awt('aw.cold_parcel_confirm_modal.change_delivery_method'),
    action: () => {
      changeDeliveryMethod();
    },
    attributes: {
      styling: 'primary-dark',
      shrink: true,
    },
  };

  const dontNeedProductButton = {
    id: 'cancel',
    label: $awt('aw.cold_parcel_confirm_modal.dont_need_product'),
    action: () => {
      closeModal();
    },
    attributes: {
      styling: 'secondary-dark',
      shrink: true,
    },
  };
  const cancelButton = {
    id: 'cancel',
    label: $awt('aw.cold_parcel_confirm_modal.cancel'),
    action: () => {
      closeModal();
    },
    attributes: {
      styling: 'secondary-dark',
      shrink: true,
    },
  };


  const opened = computed(() => modal.value.show);
  const errorData = computed(() => modal.value.data?.errorData);
  const mainTitle = computed(() => errorData.value?.mainTitle);
  const fromModal = computed(() => errorData.value?.fromModal);

  function removeDuplicateItems (errors) {
    const resultArray = [];

    errors?.forEach((item) => {
      if (!resultArray.find(resultItem => resultItem?.referenceId === item?.referenceId)) {
        resultArray.push(item);
      }
    });
    return resultArray;
  }

  const itemErrors = computed(() => {
    const errors = errorData.value.itemReferences?.map(itemReference => ({
      ...itemReference,
      id: itemReference.objectId,
      variant: {
        media: itemReference.media,
        name: itemReference.name,
        id: itemReference.objectId,
        productId: itemReference.objectId,
        cartInfo: {
          quantityStepSize: itemReference?.quantity?.quantity ?? 1,
          quantitySteps: 1,
        },
      },
    }))
      .filter(item => item);
    return removeDuplicateItems(errors);
  });

  const mappedTimeframes = computed(() => {
    return formatTimeframesForSelect(
      timeframes.value,
      $awt,
      $date,
      'aw.cold_parcel_confirm_modal.choose_time_frame',
    );
  });

  const notificationText = computed(() => {
    if (fromModal.value) {
      if (timeframes.value?.length > 0) {
        return $awt('aw.cold_parcel_confirm_modal.from_dm_modal.has_another_timeframes.description');
      }
      return mainTitle.value;
    }
    if (itemErrors.value?.length > 0) {
      if (timeframes.value?.length > 0) {
        return $awt('aw.cold_parcel_confirm_modal.has_items.has_another_timeframes.description');
      }
      return $awt('aw.cold_parcel_confirm_modal.has_items.no_another_timeframes.description');
    }
    if (timeframes.value?.length > 0) {
      return $awt('aw.cold_parcel_confirm_modal.no_items.has_another_timeframes.description');
    }
    return mainTitle.value;

  });


  const buttons = computed(() => {
    if (timeframes.value?.length > 0) {
      return [saveTimeFrameButton.value, itemErrors.value.length > 0 ? dontNeedProductButton : cancelButton];
    }
    return [changeDeliveryMethodButton, cancelButton];
  });

  function callAction () {
    modalStore.resolveColdParcelConfirmModal('accept');
  }

  function closeModal () {
    modalStore.resolveColdParcelConfirmModal('cancel');
  }

  async function saveTimeframe () {
    await saveSelectedTimeframe(selectedTimeframe.value, timeframes.value, 'cold-parcel-confirm-modal-save-timeframe', callAction);
  }

  async function changeDeliveryMethod () {
    modalStore.resolveColdParcelConfirmModal('cancel');
    if (useUserInterfaceStore().isCheckoutPage) {
      await navigateTo($localePath('shop-basket'));
    }
    modalStore.openDeliveryMethodModal({ step: modalSteps.DELIVERY_METHOD });
  }

  watch(mappedTimeframes, (newTimeframes) => {
    if (newTimeframes.length > 0 && !selectedTimeframe.value) {
      selectedTimeframe.value = newTimeframes[0]; // Set to default option
    }
  }, { immediate: true });

  watch(opened, async (newValue) => {
    if (newValue) {
      isLoading.value = true;

      try {
        const firstError = itemErrors?.value?.[0];

        // set query params
        const queryParams = new URLSearchParams();

        // add Coolomat locker ID if exists
        if (deliveryMethod.value?.deliveryMethodType === DM_DELIVERY_COOLOMAT_LOCKER && deliveryMethod.value?.lockerId) {
          queryParams.append('coolomatId', deliveryMethod.value.lockerId);
        }

        if (firstError?.name) {
          queryParams.append('objectType', firstError.name);
        }
        if (firstError?.objectId) {
          queryParams.append('objectId', firstError.objectId);
        }
        if (firstError?.quantity?.quantity) {
          queryParams.append('quantity', firstError.quantity.quantity);
        }
        if (firstError?.quantity?.type) {
          queryParams.append('quantityType', firstError.quantity.type);
        }

        const queryString = queryParams.toString();
        timeframes.value = await $api.$get(`delivery_time_frames${queryString ? '?' + queryString : ''}`);
      } catch (error) {
        $logger.error('Error fetching timeframes:', error);
      } finally {
        isLoading.value = false;
      }
    }
  }, { immediate: true });


</script>

<style module lang="scss" rel="stylesheet/scss">
.productWrapper {
  overflow-y: auto;
  max-height: 450px;
  margin-top: 32px;
  border: 1px solid $color-mercury-alt;
  border-radius: 8px;
}

.timeframeSelection {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding-top: 20px;
  padding-bottom: 0;

  @include tablet(min) {
    padding-top: 40px;
  }

  .timeframeSelector {
    width: max-content;
  }

  &InfoText {
    padding-top: 20px;
    text-align: center;
    @include font($font-weight-bold-v2, 18px, 24px);
    @include tablet(min) {
      padding-top: 40px;
    }
  }
}

.buttonContainer {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  @include tablet(min) {
    flex-direction: row;
  }
}
</style>
